import React, { useEffect, useState } from "react";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import Select from "react-select";
import Sidebar from "../Sidebar";
import Layout from "../Layout";
import Modal from "../Model";
import Loader from "../../Loader";
import { useAuthz } from "../../auth/useAuthz";
import PaymentForm from "./PaymentForm";
import PaymentsTable from "./PaymentsTable";
import { GET_PAYMENTS, formatRupees } from "./paymentQueries";

const GET_DEALER_LEDGER = gql`
  query getDealerLedger {
    getDealerLedger {
      id
      name
      billCount
      totalAmount
      paidAmount
      dueAmount
      dueCount
      oldestDueDate
      lastPaymentDate
    }
  }
`;

const MERGE_DEALERS = gql`
  mutation mergeDealers($fromId: ID!, $toId: ID!) {
    mergeDealers(fromId: $fromId, toId: $toId) {
      status
      message
    }
  }
`;

const DAY_MS = 24 * 60 * 60 * 1000;

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-IN") : "-";

const MergeDealerForm = ({ dealer, dealers, onClose, onMerged }) => {
  const [target, setTarget] = useState(null);
  const [mergeDealers, { loading }] = useMutation(MERGE_DEALERS);

  const handleMerge = async () => {
    if (!target) {
      alert("Please choose the dealer to keep");
      return;
    }

    try {
      const res = await mergeDealers({
        variables: { fromId: dealer.id, toId: target.value },
      });
      const result = res.data.mergeDealers;

      if (result.status === "SUCCESS") {
        onClose();
        onMerged();
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error("Merge dealers failed:", error);
      alert("Unable to merge the dealers. Please try again.");
    }
  };

  return (
    <div className="flex flex-col space-y-4 text-gray-800">
      <h3 className="text-xl font-semibold text-[#FBB033] border-b pb-3">
        Merge Dealer
      </h3>
      <p className="text-sm text-gray-600">
        All {dealer.billCount} bills of <b>{dealer.name}</b> will move to the
        dealer you choose, and {dealer.name} will be removed from the list.
      </p>
      <Select
        options={dealers
          .filter((option) => option.id !== dealer.id)
          .map((option) => ({ value: option.id, label: option.name }))}
        value={target}
        onChange={setTarget}
        placeholder="Dealer to keep"
        className="text-sm"
      />
      <div className="flex justify-end gap-3 pt-3 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-600"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={loading}
          onClick={handleMerge}
          className="bg-[#FBB033] px-5 py-2 rounded-md font-semibold text-black disabled:opacity-60"
        >
          Merge
        </button>
      </div>
    </div>
  );
};

const DealerLedger = () => {
  const { can } = useAuthz();
  const { data, loading, error, refetch } = useQuery(GET_DEALER_LEDGER, {
    fetchPolicy: "network-only",
  });
  const [fetchBills, { loading: billsLoading }] = useLazyQuery(GET_PAYMENTS, {
    fetchPolicy: "network-only",
  });

  const [search, setSearch] = useState("");
  const [onlyDue, setOnlyDue] = useState(false);
  const [selected, setSelected] = useState(null);
  const [bills, setBills] = useState([]);
  const [mergingDealer, setMergingDealer] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const dealers = data?.getDealerLedger || [];
  const visibleDealers = dealers.filter(
    (dealer) =>
      dealer.name.toLowerCase().includes(search.trim().toLowerCase()) &&
      (!onlyDue || dealer.dueAmount > 0),
  );

  const totalDue = dealers.reduce((sum, dealer) => sum + dealer.dueAmount, 0);
  const totalPurchased = dealers.reduce(
    (sum, dealer) => sum + dealer.totalAmount,
    0,
  );
  const dealersWithDue = dealers.filter((dealer) => dealer.dueAmount > 0);

  const loadBills = async (dealerName) => {
    const res = await fetchBills({
      variables: { data: { category: "Inventory Purchase", dealerName } },
    });
    setBills(res?.data?.getPayments?.payments || []);
  };

  useEffect(() => {
    if (selected) {
      loadBills(selected);
    }
  }, [selected]);

  const refresh = () => {
    refetch();
    if (selected) {
      loadBills(selected);
    }
  };

  const summaryCards = [
    ["Total Purchased", formatRupees(totalPurchased), "text-blue-600"],
    ["Total Due", formatRupees(totalDue), "text-amber-600"],
    ["Dealers With Due", dealersWithDue.length, "text-gray-800"],
  ];

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h2 className="text-2xl font-bold text-[#FBB033]">Dealer Ledger</h2>
          <Link
            to="/admin/payments"
            className="px-5 py-2 border border-[#FBB033] text-[#FBB033] rounded-md font-semibold whitespace-nowrap hover:bg-[#FBB033] hover:text-black transition"
          >
            Back to Payments
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          {summaryCards.map(([label, value, color]) => (
            <div key={label} className="bg-white shadow rounded-xl p-5">
              <p className="text-gray-500">{label}</p>
              <h2 className={`text-2xl font-bold ${color}`}>{value}</h2>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-4">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search dealer"
            className="flex-1 min-w-[220px] border rounded-lg px-3 py-2 bg-white text-gray-800 outline-none"
          />
          <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
            <input
              type="checkbox"
              checked={onlyDue}
              onChange={(e) => setOnlyDue(e.target.checked)}
            />
            Only dealers with due
          </label>
        </div>

        {loading && <Loader />}
        {error && (
          <div className="rounded-lg border border-red-200 bg-white p-8 text-center text-red-600">
            Error fetching the dealer ledger.
          </div>
        )}

        {!loading && !error && (
          <div className="rounded-lg overflow-x-auto bg-white shadow mb-6">
            <table className="w-full text-sm text-gray-800">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-3">Dealer</th>
                  <th className="px-4 py-3">Bills</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Paid</th>
                  <th className="px-4 py-3">Due</th>
                  <th className="px-4 py-3">Oldest Due</th>
                  <th className="px-4 py-3">Last Payment</th>
                  {can("payments.edit") && <th className="px-4 py-3" />}
                </tr>
              </thead>
              <tbody>
                {visibleDealers.map((dealer) => (
                  <tr
                    key={dealer.id}
                    onClick={() => setSelected(dealer.name)}
                    className={`border-t cursor-pointer hover:bg-gray-50 ${
                      selected === dealer.name ? "bg-amber-50" : ""
                    }`}
                  >
                    <td className="px-4 py-3 font-medium">{dealer.name}</td>
                    <td className="px-4 py-3">{dealer.billCount}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {formatRupees(dealer.totalAmount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {formatRupees(dealer.paidAmount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-semibold text-amber-600">
                      {dealer.dueAmount > 0
                        ? `${formatRupees(dealer.dueAmount)} (${dealer.dueCount})`
                        : "-"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {dealer.oldestDueDate
                        ? `${formatDate(dealer.oldestDueDate)} (${Math.floor(
                            (Date.now() -
                              new Date(dealer.oldestDueDate).getTime()) /
                              DAY_MS,
                          )} days)`
                        : "-"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {formatDate(dealer.lastPaymentDate)}
                    </td>
                    {can("payments.edit") && (
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setMergingDealer(dealer);
                          }}
                          className="text-sm text-blue-600 underline"
                        >
                          Merge
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selected && (
          <div>
            <h3 className="font-semibold text-gray-200 mb-3">
              Bills from {selected}
            </h3>
            {billsLoading || bills.length > 0 ? (
              <PaymentsTable
                data={bills}
                setOpenModal={setOpenModal}
                setEditData={setEditData}
                paymentsLoading={billsLoading}
                onDeleteSuccess={refresh}
                category="Inventory Purchase"
              />
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
                No bills from this dealer.
              </div>
            )}
          </div>
        )}

        {openModal && (
          <Modal
            onClose={() => setOpenModal(false)}
            disableOutsideClick={true}
          >
            <PaymentForm
              setOpenModal={setOpenModal}
              existingPayment={editData}
              onSuccess={refresh}
            />
          </Modal>
        )}

        {mergingDealer && (
          <Modal
            onClose={() => setMergingDealer(null)}
            disableOutsideClick={true}
          >
            <MergeDealerForm
              dealer={mergingDealer}
              dealers={dealers}
              onClose={() => setMergingDealer(null)}
              onMerged={() => {
                if (selected === mergingDealer.name) {
                  setSelected(null);
                  setBills([]);
                }
                refresh();
              }}
            />
          </Modal>
        )}
      </Layout>
    </div>
  );
};

export default DealerLedger;
